import { useState, useCallback, useMemo } from 'react';
const DEFAULT_MIN_PULL_DISTANCE = 60;
/**
 * Use this hook for a pull down gesture, e.g. to refresh a page
 *
 * @example
 * const {marginTop, onTouchStart, onTouchMove, onTouchEnd} = usePull({onPullDown: someAction});
 * <Box mt={marginTop} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} />
 *
 * The "actual" pull distance (verticalLengthOfOngoingTouch) is transformed into the marginTop we want to apply to the pulled element
 */
export const usePull = ({ onPullDown, minPullDistance = DEFAULT_MIN_PULL_DISTANCE, preventFromRefreshing, }) => {
    const [touchStart, setTouchStart] = useState(0);
    const [verticalLengthOfOngoingTouch, setVerticalLengthOfOngoingTouch] = useState(0);
    const marginTop = useMemo(() => {
        const distanceVert = touchStart - verticalLengthOfOngoingTouch;
        const isDownPull = distanceVert < -minPullDistance;
        if (isDownPull) {
            return verticalLengthOfOngoingTouch / 12;
        }
        else {
            return 0;
        }
    }, [verticalLengthOfOngoingTouch, minPullDistance, touchStart]);
    const onTouchStart = useCallback((e) => {
        setVerticalLengthOfOngoingTouch(0);
        if (e.targetTouches && e.targetTouches[0]) {
            setTouchStart(e.targetTouches[0].clientY);
        }
    }, [setVerticalLengthOfOngoingTouch, setTouchStart]);
    const onTouchMove = useCallback((e) => {
        if (e.targetTouches && e.targetTouches[0]) {
            setVerticalLengthOfOngoingTouch(e.targetTouches[0].clientY);
        }
    }, [setVerticalLengthOfOngoingTouch]);
    const onTouchEnd = useCallback(() => {
        if (!touchStart || !verticalLengthOfOngoingTouch) {
            return;
        }
        const distanceVert = touchStart - verticalLengthOfOngoingTouch;
        const isDownPull = distanceVert < -minPullDistance;
        if (isDownPull) {
            onPullDown();
            setVerticalLengthOfOngoingTouch(0);
        }
    }, [touchStart, verticalLengthOfOngoingTouch, minPullDistance, setVerticalLengthOfOngoingTouch, onPullDown]);
    if (preventFromRefreshing) {
        return {
            onTouchStart: () => {
                // noop
            },
            onTouchMove: () => {
                // noop
            },
            onTouchEnd: () => {
                // noop
            },
            marginTop: 0,
        };
    }
    return {
        onTouchStart,
        onTouchMove,
        onTouchEnd,
        marginTop,
    };
};
